/**
 * Set Member Card
 * Allows setting the current on-call member from available members
 */

import React, { useState, useMemo } from 'react';
import {
  Stack,
  Text,
  Dropdown,
  type IDropdownOption,
  PrimaryButton,
  DefaultButton,
  MessageBarType,
  Spinner,
  SpinnerSize,
  useTheme
} from '@fluentui/react';
import { useOnCallManager } from '../contexts/OnCallManagerContext';

interface SetMemberCardProps {
  onNotification: (type: MessageBarType, message: string) => void;
}

const SetMemberCard: React.FC<SetMemberCardProps> = ({ onNotification }) => {
  const { 
    availableMembers, 
    onCallMembers, 
    setCurrentMember, 
    sendTestNotification,
    loading 
  } = useOnCallManager();
  
  const [selectedMemberId, setSelectedMemberId] = useState<string | undefined>(undefined);
  const [isSetting, setIsSetting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const theme = useTheme();

  // Filter available members to only those in the on-call list
  const onCallMemberOptions = useMemo<IDropdownOption[]>(() => {
    const filtered = availableMembers.filter(m => onCallMembers.includes(m.memberId));
    return filtered.map(member => ({
      key: member.memberId,
      text: member.fullName,
      data: member
    }));
  }, [availableMembers, onCallMembers]);

  const handleSetOnCall = async () => {
    if (!selectedMemberId) {
      onNotification(MessageBarType.error, 'Please select a member to set as on-call.');
      return;
    }

    setIsSetting(true);
    try {
      await setCurrentMember(selectedMemberId);
      const member = availableMembers.find(m => m.memberId === selectedMemberId);
      onNotification(MessageBarType.success, `${member?.fullName} has been set as the on-call member.`);
      setSelectedMemberId(undefined);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to set on-call member';
      onNotification(MessageBarType.error, `Error: ${message}`);
    } finally {
      setIsSetting(false);
    }
  };

  const handleTestNotification = async () => {
    setIsTesting(true);
    try {
      await sendTestNotification();
      onNotification(MessageBarType.success, 'Test notification sent successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send test notification';
      onNotification(MessageBarType.error, `Error: ${message}`);
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Stack
      tokens={{ childrenGap: 16, padding: 20 }}
      styles={{
        root: {
          height: '100%',
          backgroundColor: theme.semanticColors.bodyBackground,
          color: theme.semanticColors.bodyText,
          borderRadius: 4,
          boxShadow: '0 1.6px 3.6px 0 rgba(0,0,0,0.132), 0 0.3px 0.9px 0 rgba(0,0,0,0.108)',
          selectors: {
            '.ms-Text': { color: theme.semanticColors.bodyText },
            '.ms-Label': { color: theme.semanticColors.bodyText }
          }
        }
      }}
    >
      <Stack tokens={{ childrenGap: 16 }}>
        <Text variant="xLarge" styles={{ root: { fontWeight: 600, color: theme.semanticColors.bodyText } }}>
          Set On-Call Member
        </Text>

        {loading ? (
          <Spinner size={SpinnerSize.medium} label="Loading members..." />
        ) : (
          <>
            <Dropdown
              placeholder="Select a member"
              label="Choose from on-call list"
              options={onCallMemberOptions}
              selectedKey={selectedMemberId}
              onChange={(_, option) => setSelectedMemberId(option?.key as string)}
              disabled={isSetting || onCallMemberOptions.length === 0}
              styles={{
                title: { color: theme.semanticColors.bodyText },
                label: { color: theme.semanticColors.bodyText },
                dropdownItem: { color: theme.semanticColors.bodyText },
                dropdownItemSelected: { color: theme.semanticColors.bodyText }
              }}
            />

            {onCallMemberOptions.length === 0 && (
              <Text variant="small" styles={{ root: { color: theme.semanticColors.disabledText } }}>
                No members in the on-call list. Configure the on-call members list below.
              </Text>
            )}

            <Stack horizontal tokens={{ childrenGap: 12 }}>
              <PrimaryButton
                text={isSetting ? 'Setting...' : 'Set On-Call'}
                onClick={handleSetOnCall}
                disabled={!selectedMemberId || isSetting || loading}
              />
              <DefaultButton
                text={isTesting ? 'Sending...' : 'Test Notification'}
                onClick={handleTestNotification}
                disabled={isTesting || loading}
              />
            </Stack>
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default SetMemberCard;
